import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useSidebar } from "@/context/SidebarContext";
import {
  Search,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  CircleCheck,
  Send,
  FileText,
  Loader2,
  MessageCircle,
  Lock,
  RotateCcw,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const FILTERS = ["All", "Unread", "Open", "Resolved"];
const inputCls =
  "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-400";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "2-digit", month: "short" });
}

function fillPlaceholders(text, variables) {
  if (!text) return "";
  return text.replace(/{{\s*(\d+)\s*}}/g, (_, n) => {
    const index = parseInt(n, 10) - 1;
    return (variables && variables[index]) || `{{${n}}}`;
  });
}

function StatusTicks({ status }) {
  if (status === "queued")
    return <Clock className="h-3.5 w-3.5 text-white/70" data-testid="tick-queued" />;
  if (status === "sent")
    return <Check className="h-3.5 w-3.5 text-white/70" data-testid="tick-sent" />;
  if (status === "delivered")
    return <CheckCheck className="h-3.5 w-3.5 text-white/70" data-testid="tick-delivered" />;
  if (status === "read")
    return <CheckCheck className="h-3.5 w-3.5 text-sky-300" data-testid="tick-read" />;
  if (status === "failed")
    return <AlertCircle className="h-3.5 w-3.5 text-red-200" data-testid="tick-failed" />;
  return null;
}

// Labels the backend saves in message_text when a message has no real text.
const PLACEHOLDER_TEXT = /^\[(image|video|audio|document|sticker)( attached)?\]$/;

// Blob URLs of member media already downloaded, so switching chats doesn't refetch.
const mediaCache = {};

function MediaView({ type, src, filename, voice }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <p className="text-xs italic opacity-70">🖼️ Image no longer available</p>;
  if (type === "image" || type === "sticker") {
    return (
      <a href={src} target="_blank" rel="noreferrer">
        <img
          src={src}
          alt=""
          onError={() => setBroken(true)}
          className={`rounded-md mb-1 ${type === "sticker" ? "w-32 h-32 object-contain" : "max-h-72 w-auto"}`}
        />
      </a>
    );
  }
  if (type === "video") return <video src={src} controls className="rounded-md mb-1 max-h-72" />;
  if (type === "audio") {
    return (
      <div className="mb-1">
        <p className="text-[10px] opacity-70 mb-0.5">{voice ? "🎤 Voice note" : "🎵 Audio"}</p>
        <audio src={src} controls className="max-w-[260px]" />
      </div>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      download={filename || true}
      className="flex items-center gap-2 bg-black/20 hover:bg-black/30 rounded-md px-3 py-2 mb-1 underline-offset-2 hover:underline"
    >
      <FileText className="h-4 w-4 shrink-0" />
      <span className="truncate">{filename || "Document"}</span>
    </a>
  );
}

// Member media lives on Meta and needs the branch token, so it is loaded via our backend.
function InboundMedia({ message }) {
  const media = message.media;
  const autoLoad = ["image", "sticker", "audio"].includes(media.type);
  const [src, setSrc] = useState(mediaCache[message.id] || null);
  const [state, setState] = useState("idle"); // idle | loading | error

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await api.get(`/messages/${message.id}/media`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      mediaCache[message.id] = url;
      setSrc(url);
      setState("idle");
    } catch {
      setState("error");
    }
  }, [message.id]);

  useEffect(() => {
    if (!src && autoLoad) load();
  }, [src, autoLoad, load]);

  if (src) return <MediaView type={media.type} src={src} filename={media.filename} voice={media.voice} />;
  if (state === "loading") {
    return (
      <p className="flex items-center gap-1.5 text-xs opacity-70 mb-1">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading {media.type}…
      </p>
    );
  }
  if (state === "error") return <p className="text-xs italic opacity-70 mb-1">⚠️ {media.type} no longer available</p>;
  return (
    <button
      type="button"
      onClick={load}
      className="flex items-center gap-2 bg-black/20 hover:bg-black/30 rounded-md px-3 py-2 mb-1 text-left"
    >
      <FileText className="h-4 w-4 shrink-0" />
      <span className="truncate">
        {media.type === "video" ? "▶ Load video" : `Open ${media.filename || "document"}`}
      </span>
    </button>
  );
}

function MessageExtras({ m, quoted }) {
  return (
    <>
      {quoted && (
        <div className="border-l-2 border-white/40 bg-black/20 rounded px-2 py-1 mb-1 text-[11px] opacity-80 line-clamp-2">
          {quoted.message_text || (quoted.template_name ? `📄 ${quoted.template_name}` : "Message")}
        </div>
      )}
      {m.header_image_url && <MediaView type="image" src={m.header_image_url} />}
      {m.media?.url && (
        <MediaView type={m.media.type} src={m.media.url} filename={m.media.url.split("/").pop()} />
      )}
      {m.media?.id && <InboundMedia message={m} />}
      {m.location && (
        <a
          href={`https://www.google.com/maps?q=${m.location.latitude},${m.location.longitude}`}
          target="_blank"
          rel="noreferrer"
          className="block bg-black/20 hover:bg-black/30 rounded-md px-3 py-2 mb-1 underline-offset-2 hover:underline"
        >
          📍 {m.location.name || "Location"}
          {m.location.address && <span className="block text-[11px] opacity-70">{m.location.address}</span>}
          <span className="block text-[10px] opacity-60">Open in Google Maps</span>
        </a>
      )}
    </>
  );
}

export default function WAChat() {
  const { activeBranchId } = useBranch();
  const { collapsed } = useSidebar();
  const sideOffset = collapsed ? "left-[72px]" : "left-64";
  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [usedTemplates, setUsedTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null); // {conversation, messages}
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [staff, setStaff] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState({ name: "", language: "en", variables: "" });
  const [tplSending, setTplSending] = useState(false);
  const [resendingId, setResendingId] = useState("");
  const msgsRef = useRef(null);

  const loadConversations = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const { data } = await api.get("/conversations", {
        params: {
          branch_id: activeBranchId,
          filter: filter.toLowerCase(),
          search,
          template: templateFilter === "all" ? "" : templateFilter,
        },
      });
      setConversations(data);
    } catch (e) {
      /* ignore */
    }
  }, [activeBranchId, filter, search, templateFilter]);

  const loadUsedTemplates = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const { data } = await api.get("/conversations/used-templates", {
        params: { branch_id: activeBranchId },
      });
      setUsedTemplates(data);
    } catch (e) {
      setUsedTemplates([]);
    }
  }, [activeBranchId]);

  const loadTemplates = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const { data } = await api.get("/templates", { params: { branch_id: activeBranchId } });
      setTemplates(data);
    } catch (e) {
      setTemplates([]);
    }
  }, [activeBranchId]);

  const loadStaff = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const { data } = await api.get(`/branches/${activeBranchId}/staff`);
      setStaff(data);
    } catch (e) {
      setStaff([]);
    }
  }, [activeBranchId]);

  const loadThread = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/conversations/${cid}/messages`);
      setThread(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  // reset selection when branch changes
  useEffect(() => {
    setSelectedId(null);
    setThread(null);
    loadStaff();
    loadUsedTemplates();
    loadTemplates();
  }, [activeBranchId, loadStaff, loadUsedTemplates, loadTemplates]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (selectedId) loadThread(selectedId);
  }, [selectedId, loadThread]);

  // open a conversation requested from the Dashboard (after list loads)
  useEffect(() => {
    const pending = localStorage.getItem("open_conversation");
    if (pending) {
      setSelectedId(pending);
      localStorage.removeItem("open_conversation");
    }
  }, []);

  // light polling
  useEffect(() => {
    const t = setInterval(() => {
      loadConversations();
      if (selectedId) loadThread(selectedId);
    }, 8000);
    return () => clearInterval(t);
  }, [loadConversations, loadThread, selectedId]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const conv = thread?.conversation;
  const contact = conv?.contact;

  const windowOpen = useMemo(() => {
    if (!conv?.last_inbound_at) return false;
    const diff = Date.now() - new Date(conv.last_inbound_at).getTime();
    return diff < 24 * 60 * 60 * 1000;
  }, [conv?.last_inbound_at]);

  const templateByName = useMemo(() => {
    const map = {};
    templates.forEach((t) => {
      map[t.name] = t;
    });
    return map;
  }, [templates]);

  // lets a member's "reply" show which earlier message they quoted
  const msgByProviderId = useMemo(() => {
    const map = {};
    (thread?.messages || []).forEach((m) => {
      if (m.provider_message_id) map[m.provider_message_id] = m;
    });
    return map;
  }, [thread]);

  const openConversation = (c) => {
    setSelectedId(c.id);
    // optimistic unread clear
    setConversations((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, unread_count: 0 } : x))
    );
  };

  const sendReply = async () => {
    // Ref, not state: a fast second Enter/click runs before `sending` re-renders,
    // which used to send the same reply twice.
    if (!reply.trim() || !contact || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      await api.post("/whatsapp/send", {
        branch_id: activeBranchId,
        mobile: contact.mobile,
        message_text: reply.trim(),
      });
      setReply("");
      await loadThread(selectedId);
      await loadConversations();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const sendTemplate = async () => {
    if (!tpl.name.trim() || !contact) return;
    setTplSending(true);
    try {
      const variables = tpl.variables
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      await api.post("/whatsapp/send", {
        branch_id: activeBranchId,
        mobile: contact.mobile,
        template_name: tpl.name.trim(),
        language: tpl.language.trim() || "en",
        variables,
      });
      setTplOpen(false);
      setTpl({ name: "", language: "en", variables: "" });
      await loadThread(selectedId);
      await loadConversations();
      toast.success("Template sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTplSending(false);
    }
  };

  const resolveConversation = async () => {
    const next = conv.status === "resolved" ? "open" : "resolved";
    try {
      await api.put(`/conversations/${selectedId}`, { status: next });
      await loadThread(selectedId);
      await loadConversations();
      toast.success(next === "resolved" ? "Conversation resolved" : "Conversation reopened");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleOptedOut = async (val) => {
    try {
      await api.put(`/contacts/${contact.id}`, { opted_out: val });
      await loadThread(selectedId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleBotPaused = async (val) => {
    try {
      await api.put(`/conversations/${selectedId}`, { bot_paused: val });
      await loadThread(selectedId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assignTo = async (val) => {
    try {
      await api.put(`/conversations/${selectedId}`, {
        assigned_user_id: val === "unassigned" ? null : val,
      });
      await loadThread(selectedId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resendMessage = async (messageId) => {
    setResendingId(messageId);
    try {
      await api.post(`/messages/${messageId}/retry`);
      toast.success("Resent");
      await loadThread(selectedId);
      await loadConversations();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResendingId("");
    }
  };

  function renderMessageBody(m) {
    // media / location are drawn by MessageExtras; only keep a real caption here
    if (m.location) return "";
    if (m.media) return m.media.caption || (PLACEHOLDER_TEXT.test(m.message_text || "") ? "" : m.message_text || "");
    if (m.message_text === "[unsupported]") {
      return "⚠️ Member sent a message WhatsApp doesn't share with apps (e.g. view-once, poll or edited message). Check it on the phone.";
    }
    if (m.message_text) return m.message_text;

    if (m.template_name) {
      const template = templateByName[m.template_name];
      if (template?.body_text) {
        const filled = fillPlaceholders(template.body_text, m.variables);
        const headerNote = template.header_type === "IMAGE" && !m.header_image_url ? "🖼️ " : "";
        return `${headerNote}${filled}`;
      }
      return `📄 Template: ${m.template_name}`;
    }

    return "";
  }

  return (
    <div
      data-testid="wa-chat-page"
      className={`fixed top-16 ${sideOffset} right-0 bottom-0 flex flex-col bg-[#0a0a0a] overflow-hidden transition-[left] duration-300 ease-in-out`}
    >
      <div className="grid grid-cols-[320px_1fr_300px] h-full overflow-hidden">
        {/* LEFT: conversation list */}
        <div className="border-r border-white/10 flex flex-col bg-[#0d0d0d] min-h-0">
          <div className="p-4 border-b border-white/10 bg-[#0a0a0a] space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="conversation-search-input"
                className={`${inputCls} pl-9`}
                placeholder="Search name, mobile, or message text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger data-testid="template-filter-select" className="w-full">
                <SelectValue placeholder="Filter by template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Templates</SelectItem>
                {usedTemplates.map((name) => (
                  <SelectItem key={name} value={name} data-testid={`template-filter-opt-${name}`}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`filter-tab-${f.toLowerCase()}`}
                  onClick={() => setFilter(f)}
                  className={`text-xs font-semibold px-2.5 py-1.5 rounded-md transition-all duration-150 ${
                    filter === f
                      ? "bg-orange-600 text-white"
                      : "text-slate-400 hover:bg-white/10"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">No conversations</div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  data-testid={`conversation-row-${c.id}`}
                  onClick={() => openConversation(c)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 transition-all duration-150 ${
                    selectedId === c.id ? "bg-orange-500/10" : "hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-base text-white truncate flex items-center gap-2">
                      <WhatsAppIcon className="h-4 w-4 shrink-0" />
                      {c.last_message_status === "failed" && (
                        <span
                          data-testid={`failed-dot-${c.id}`}
                          className="h-2 w-2 rounded-full bg-red-500 shrink-0"
                          title="Last message failed"
                        />
                      )}
                      {c.contact?.name}
                    </span>
                    <span className="text-xs font-semibold text-slate-400 shrink-0">
                      {fmtTime(c.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="text-sm text-slate-400 truncate">
                      {c.last_message_direction === "outbound" ? "You: " : ""}
                      {c.last_message_preview || c.contact?.mobile}
                    </span>
                    {c.unread_count > 0 && (
                      <span
                        data-testid={`unread-badge-${c.id}`}
                        className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-600 text-white text-[10px] font-bold flex items-center justify-center"
                      >
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                  {c.status === "resolved" && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] tracking-wide uppercase font-bold text-green-400">
                      <CircleCheck className="h-3 w-3" /> Resolved
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* MIDDLE: thread */}
        <div className="flex flex-col bg-[#0a0a0a] min-w-0 min-h-0">
          {!conv ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
              <MessageCircle className="h-10 w-10 mb-3" />
              <p className="text-sm">Select a conversation to start</p>
            </div>
          ) : (
            <>
              <div className="h-16 border-b border-white/10 flex items-center justify-between px-6 shrink-0">
                <div>
                  <p className="font-heading font-bold text-lg text-white flex items-center gap-2">
                    <WhatsAppIcon className="h-5 w-5 shrink-0" />
                    {contact?.name}
                  </p>
                  <p className="text-sm text-slate-400">{contact?.mobile}</p>
                </div>
                <button
                  data-testid="resolve-button"
                  onClick={resolveConversation}
                  className={`flex items-center gap-2 text-sm font-semibold rounded-md px-3 py-1.5 transition-all duration-150 ${
                    conv.status === "resolved"
                      ? "bg-white/10 hover:bg-white/15 text-slate-200"
                      : "bg-orange-600 hover:bg-orange-700 text-white"
                  }`}
                >
                  <CircleCheck className="h-4 w-4" />
                  {conv.status === "resolved" ? "Reopen" : "Resolve"}
                </button>
              </div>

              <div ref={msgsRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-3 bg-[#0d0d0d]">
                <AnimatePresence initial={false}>
                {thread.messages.map((m) => (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`message-${m.id}`}
                    className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg px-4 py-2.5 text-[15px] leading-relaxed ${
                        m.direction === "outbound"
                          ? "bg-orange-600 text-white rounded-br-sm"
                          : "bg-[#1f2c34] border border-white/10 text-white rounded-bl-sm"
                      }`}
                    >
                      {m.is_bot && (
                        <span className="inline-block text-[9px] font-bold uppercase tracking-wide bg-white/25 rounded px-1.5 py-0.5 mb-1">Bot</span>
                      )}
                      <MessageExtras
                        m={m}
                        quoted={m.reply_to_provider_id && msgByProviderId[m.reply_to_provider_id]}
                      />
                      {renderMessageBody(m) && (
                        <p className="whitespace-pre-wrap break-words">{renderMessageBody(m)}</p>
                      )}
                      <div
                        className={`flex items-center gap-1 mt-1 justify-end ${
                          m.direction === "outbound" ? "text-white/70" : "text-slate-400"
                        }`}
                      >
                        <span className="text-[11px]">{fmtTime(m.created_at)}</span>
                        {m.direction === "outbound" && <StatusTicks status={m.status} />}
                      </div>
                      {m.direction === "outbound" && m.status === "failed" && (
                        <div className="mt-1.5 flex items-center justify-between gap-2">
                          {m.failure_reason && (
                            <p className="text-[10px] text-red-200">{m.failure_reason}</p>
                          )}
                          <button
                            type="button"
                            data-testid={`resend-button-${m.id}`}
                            onClick={() => resendMessage(m.id)}
                            disabled={resendingId === m.id}
                            className="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-white/20 hover:bg-white/30 rounded px-2 py-1 transition-all duration-150 disabled:opacity-60 shrink-0"
                          >
                            {resendingId === m.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3 w-3" />
                            )}
                            Resend
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
                </AnimatePresence>
              </div>

              {/* Reply box */}
              <div className="border-t border-white/10 p-4 shrink-0 bg-[#0a0a0a]">
                {windowOpen ? (
                  <div className="flex items-end gap-2">
                    <textarea
                      data-testid="reply-input"
                      rows={1}
                      className={`${inputCls} resize-none`}
                      placeholder="Type a message"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <button
                      data-testid="send-reply-button"
                      onClick={sendReply}
                      disabled={sending || !reply.trim()}
                      className="bg-orange-600 hover:bg-orange-700 text-white rounded-md h-10 w-10 flex items-center justify-center transition-all duration-150 disabled:opacity-60 shrink-0"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                ) : (
                  <div
                    data-testid="window-closed-banner"
                    className="flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3"
                  >
                    <div className="flex items-center gap-2 text-sm text-amber-400">
                      <Lock className="h-4 w-4" />
                      24-hour window closed — send a template instead
                    </div>
                    <button
                      data-testid="open-template-picker-button"
                      onClick={() => setTplOpen(true)}
                      className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-3 py-1.5 transition-all duration-150 shrink-0"
                    >
                      <FileText className="h-4 w-4" />
                      Send Template
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* RIGHT: contact panel */}
        <div className="border-l border-white/10 bg-[#0a0a0a] p-5 overflow-y-auto">
          {!contact ? (
            <div className="text-sm text-slate-500 text-center mt-10">No contact selected</div>
          ) : (
            <div data-testid="contact-panel" className="space-y-6">
              <div>
                <div className="h-14 w-14 rounded-full bg-orange-500/15 flex items-center justify-center text-orange-400 font-heading font-black text-xl">
                  {(contact.name || "?").charAt(0).toUpperCase()}
                </div>
                <p className="mt-3 font-heading font-bold text-lg text-white">{contact.name}</p>
                <p className="text-sm text-slate-400">{contact.mobile}</p>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <div>
                  <p className="text-sm font-semibold text-white">Opted out of broadcasts</p>
                  <p className="text-xs text-slate-400">Exclude from bulk sends</p>
                </div>
                <Switch
                  data-testid="opted-out-switch"
                  checked={!!contact.opted_out}
                  onCheckedChange={toggleOptedOut}
                />
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <div>
                  <p className="text-sm font-semibold text-white">Pause bot</p>
                  <p className="text-xs text-slate-400">Stop auto-replies here</p>
                </div>
                <Switch
                  data-testid="pause-bot-switch"
                  checked={!!conv.bot_paused}
                  onCheckedChange={toggleBotPaused}
                />
              </div>

              <div className="border-t border-white/10 pt-4">
                <label className={labelCls}>Assign to</label>
                <Select
                  value={conv.assigned_user_id || "unassigned"}
                  onValueChange={assignTo}
                >
                  <SelectTrigger data-testid="assign-select" className="mt-1.5">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`assign-opt-${s.id}`}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {staff.length === 0 && (
                  <p className="text-xs text-slate-500 mt-2">No staff on this branch yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template picker */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send a Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Template Name</label>
              <input
                data-testid="template-name-input"
                className={`mt-1.5 ${inputCls}`}
                value={tpl.name}
                onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
                placeholder="e.g. renewal_reminder"
              />
            </div>
            <div>
              <label className={labelCls}>Language Code</label>
              <input
                data-testid="template-language-input"
                className={`mt-1.5 ${inputCls}`}
                value={tpl.language}
                onChange={(e) => setTpl({ ...tpl, language: e.target.value })}
                placeholder="en"
              />
            </div>
            <div>
              <label className={labelCls}>Variables (comma separated)</label>
              <input
                data-testid="template-variables-input"
                className={`mt-1.5 ${inputCls}`}
                value={tpl.variables}
                onChange={(e) => setTpl({ ...tpl, variables: e.target.value })}
                placeholder="John, 30th June"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              className="bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150"
              onClick={() => setTplOpen(false)}
            >
              Cancel
            </button>
            <button
              data-testid="send-template-button"
              onClick={sendTemplate}
              disabled={tplSending || !tpl.name.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 flex items-center gap-2 disabled:opacity-60"
            >
              {tplSending && <Loader2 className="h-4 w-4 animate-spin" />}
              Send
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}